import omit from 'lodash/omit';
import {
  FORM_DID_CLEAR_ERROR,
  FORM_DID_COMPLETE,
  FORM_DID_DISMISS,
  FORM_DID_END,
  FORM_DID_RESET_STAGE_DATA,
  FORM_DID_SET_DIALOG,
  FORM_DID_SET_ERROR,
  FORM_DID_SET_STAGE_INDEX,
  FORM_DID_TRANSITION_TO_NEXT,
  FORM_DID_TRANSITION_TO_PREV,
  FORM_DID_UPDATE_STAGE_DATA,
  FORM_DID_UPDATE_VALIDATION_STATUS,
  FORM_START,
} from '../../constants/actionTypes';

import * as formTypes from '../../constants/formTypes';

const initialState = {
  activeType: null,
  // when a form is opened inside another, activeType is set to the type of the
  // child form and prevType is used to remember the parent form type so that
  // when the child is closed, the parent can be made active
  prevType: null,
  [formTypes.FORM_TYPE_CREATE_DATA_SOURCE]: null,
  [formTypes.FORM_TYPE_EDIT_DATA_SOURCE]: null,
  [formTypes.FORM_TYPE_CREATE_DATASET]: null,
  [formTypes.FORM_TYPE_EDIT_DATASET]: null,
  [formTypes.FORM_TYPE_CREATE_REUSABLE_TX]: null,
  [formTypes.FORM_TYPE_CREATE_SCHEMA_SPEC]: null,
  [formTypes.FORM_TYPE_CREATE_REPLICATION]: {},
  [formTypes.FORM_TYPE_CREATE_SPARK]: {},
  [formTypes.FORM_TYPE_CREATE_CDC]: {},
  [formTypes.FORM_TYPE_CREATE_ELT]: {},
};

const newFormDefaultState = {
  isActive: false,
  currentStage: 0,
  data: null,
  initialData: null,
  error: null,
  dialogIsOpen: false,
  awaitingValidation: false,
  isComplete: false,
};

export const formsReducer = (state = initialState, action) => {
  switch (action.type) {
    case FORM_START:
      return startNewForm(state, action);

    case FORM_DID_DISMISS:
    case FORM_DID_END:
      return finishForm(state, action);

    case FORM_DID_TRANSITION_TO_NEXT: {
      let { formType } = action.payload;
      let newFormInfo = formMoveNext(state[formType], action.payload);

      return { ...state, [formType]: newFormInfo };
    }

    case FORM_DID_TRANSITION_TO_PREV: {
      let { formType } = action.payload;
      let newFormInfo = formMovePrev(state[formType], action.payload);

      return { ...state, [formType]: newFormInfo };
    }

    case FORM_DID_UPDATE_STAGE_DATA: {
      let { formType } = action.payload;
      if (!state.activeType) {
        state.activeType = formType;
      }
      let newFormData = formUpdateData({ ...state[formType] }, action.payload);

      return {
        ...state,
        [formType]: { ...newFormData },
      };
    }

    case FORM_DID_RESET_STAGE_DATA: {
      let { formType } = action.payload;
      let newFormData =
        formType === state.activeType
          ? formResetData(state[formType], action.payload)
          : state[formType];
      return {
        ...state,
        [formType]: newFormData,
      };
    }

    case FORM_DID_SET_ERROR: {
      let { formType } = action.payload;
      let newFormInfo = formSetError(state[formType], action.payload);

      return { ...state, [formType]: newFormInfo };
    }

    case FORM_DID_CLEAR_ERROR: {
      let { formType } = action.payload;
      let newFormInfo = formClearError(state[formType], action.payload);

      return { ...state, [formType]: newFormInfo };
    }

    case FORM_DID_SET_DIALOG: {
      let { formType } = action.payload;
      let newFormInfo = formSetDialog(state[formType], action.payload);

      return { ...state, [formType]: newFormInfo };
    }

    case FORM_DID_UPDATE_VALIDATION_STATUS: {
      let { formType } = action.payload;
      let newFormInfo = formUpdateValidationStatus(state[formType], action.payload);

      return { ...state, [formType]: newFormInfo };
    }

    case FORM_DID_SET_STAGE_INDEX: {
      let { formType } = action.payload;
      let newFormInfo = formMoveToStage(state[formType], action.payload);

      return { ...state, [formType]: newFormInfo };
    }

    case FORM_DID_COMPLETE: {
      let { formType } = action.payload;
      let newFormInfo = formComplete(state[formType], action.payload);

      return { ...state, [formType]: newFormInfo };
    }

    default:
      return state;
  }
};

export default formsReducer;

function startNewForm(state, action) {
  const { formType } = action.payload;
  const formInfo = { ...newFormDefaultState, isActive: true };
  const prevType = state.activeType;
  const newState = {
    ...state,
    prevType,
    activeType: formType,
    [formType]: { ...formInfo },
  };

  if (prevType !== formType) {
    newState[prevType] = {
      ...newFormDefaultState,
      ...state[prevType],
      isActive: false,
    };
  }

  return newState;
}

function finishForm(state, action) {
  const { formType } = action.payload;
  const { prevType } = state;
  const newState = {
    ...state,
    // only clear the data of the given type
    [formType]: initialState[formType],
    activeType: prevType,
    prevType: null,
  };

  // make the parent form active
  if (prevType) {
    newState[prevType] = { ...state[prevType], isActive: true };
  }

  return newState;
}

function formMoveNext(formState, payload) {
  let { currentStage } = formState;
  return { ...formState, currentStage: currentStage + 1 };
}

function formMovePrev(formState, payload) {
  let { currentStage } = formState;
  return { ...formState, currentStage: currentStage - 1 };
}

function formMoveToStage(formState, payload) {
  let { stageIndex } = payload;
  return { ...formState, currentStage: stageIndex };
}

function formUpdateData(formState, payload) {
  let { formData, resetValidationStatus } = payload;
  let newState = {
    ...formState,
    data: {
      ...(formState && formState.data),
      ...formData,
    },
  };

  // Preserve initial values for sourceConfig and apiConfig when first set
  if (!formState.initialData && (formData.sourceConfig || formData.apiConfig)) {
    newState.initialData = {
      sourceConfig: formData.sourceConfig || null,
      apiConfig: formData.apiConfig || null,
    };
  }

  if (resetValidationStatus) {
    newState.error = null;
    newState.awaitingValidation = false;
  }

  return newState;
}

function formResetData(formState, payload) {
  let { resetFields, resetValidationStatus } = payload;

  if (!resetFields) {
    return formState;
  }
  let newFormData = omit(formState.data, resetFields);
  let newState = {
    ...formState,
    data: newFormData,
  };

  if (resetValidationStatus) {
    newState.error = null;
    newState.awaitingValidation = false;
  }

  return newState;
}

function formSetError(formState, payload) {
  let { error } = payload;
  return {
    ...formState,
    error,
  };
}

function formClearError(formState, payload) {
  return {
    ...formState,
    error: null,
  };
}

function formSetDialog(formState, payload) {
  let { isOpen } = payload;
  return {
    ...formState,
    dialogIsOpen: isOpen,
  };
}

function formUpdateValidationStatus(formState, payload) {
  let { awaitingValidation } = payload;
  return {
    ...formState,
    awaitingValidation,
  };
}

function formComplete(formState, payload) {
  let { isComplete } = payload;
  return {
    ...formState,
    isComplete,
  };
}
